'use client';

import { createClient } from '@/lib/supabase/client';

export interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  productCount: number;
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  categoryId: string;
  categoryName: string;
  modelNumber: string | null;
  shortDescription: string;
  description: string;
  specifications: Array<{ param: string; value: string }>;
  applications: string[];
  advantages: string[];
  images: string[];
  badge: string | null;
  isFeatured: boolean;
  isLatest: boolean;
  isActive: boolean;
  metaTitle: string | null;
  metaDescription: string | null;
  createdAt: string;
}

export interface ProductInput {
  name: string;
  slug: string;
  category_id: string;
  category_name: string;
  model_number?: string | null;
  short_description?: string;
  description?: string;
  specifications?: Array<{ param: string; value: string }>;
  applications?: string[];
  advantages?: string[];
  images?: string[];
  badge?: string | null;
  is_featured?: boolean;
  is_latest?: boolean;
  is_active?: boolean;
  meta_title?: string | null;
  meta_description?: string | null;
}

function mapProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    categoryId: row.category_id,
    categoryName: row.category_name,
    modelNumber: row.model_number,
    shortDescription: row.short_description || '',
    description: row.description || '',
    specifications: Array.isArray(row.specifications) ? row.specifications : [],
    applications: Array.isArray(row.applications) ? row.applications : [],
    advantages: Array.isArray(row.advantages) ? row.advantages : [],
    images: Array.isArray(row.images) ? row.images : [],
    badge: row.badge,
    isFeatured: row.is_featured,
    isLatest: row.is_latest || false,
    isActive: row.is_active,
    metaTitle: row.meta_title || null,
    metaDescription: row.meta_description || null,
    createdAt: row.created_at,
  };
}

function mapCategory(row: any): ProductCategory {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description || '',
    icon: row.icon || '🔬',
    productCount: row.product_count || 0,
  };
}

export const productService = {
  async getCategories(): Promise<ProductCategory[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .order('name', { ascending: true });
      if (error) {
        console.error('Error fetching categories:', error.message);
        return [];
      }
      return (data || []).map(mapCategory);
    } catch (err: any) {
      console.error('Categories fetch failed:', err.message);
      return [];
    }
  },

  async getProducts(params?: {
    search?: string;
    categorySlug?: string;
    featured?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ products: Product[]; total: number }> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('is_active', true);

      if (params?.search) {
        query = query.or(
          `name.ilike.%${params.search}%,short_description.ilike.%${params.search}%,category_name.ilike.%${params.search}%`
        );
      }

      if (params?.categorySlug && params.categorySlug !== 'all') {
        const { data: cat } = await supabase
          .from('product_categories')
          .select('id')
          .eq('slug', params.categorySlug)
          .maybeSingle();
        if (cat?.id) {
          query = query.eq('category_id', cat.id);
        }
      }

      if (params?.featured) {
        query = query.eq('is_featured', true);
      }

      query = query.order('is_featured', { ascending: false }).order('name', { ascending: true });

      if (params?.limit) {
        const from = params.offset || 0;
        const to = from + params.limit - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;
      if (error) {
        console.error('Error fetching products:', error.message);
        return { products: [], total: 0 };
      }
      return { products: (data || []).map(mapProduct), total: count || 0 };
    } catch (err: any) {
      console.error('Products fetch failed:', err.message);
      return { products: [], total: 0 };
    }
  },

  async getProductBySlug(slug: string): Promise<Product | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();
      if (error) {
        console.error('Error fetching product:', error.message);
        return null;
      }
      return data ? mapProduct(data) : null;
    } catch (err: any) {
      console.error('Product fetch failed:', err.message);
      return null;
    }
  },

  async getRelatedProducts(categoryId: string, excludeSlug: string, limit = 3): Promise<Product[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .neq('slug', excludeSlug)
        .limit(limit);
      if (error) {
        console.error('Error fetching related products:', error.message);
        return [];
      }
      return (data || []).map(mapProduct);
    } catch (err: any) {
      console.error('Related products fetch failed:', err.message);
      return [];
    }
  },

  async getFeaturedProducts(limit = 8): Promise<Product[]> {
    const result = await productService.getProducts({ featured: true, limit });
    return result.products;
  },

  async getLatestProducts(limit = 12): Promise<Product[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data || []).map(mapProduct);
  },

  // ── ADMIN METHODS ──

  async getAdminProducts(params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ products: Product[]; total: number }> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('products')
        .select('*', { count: 'exact' });

      if (params?.search) {
        query = query.or(
          `name.ilike.%${params.search}%,category_name.ilike.%${params.search}%,model_number.ilike.%${params.search}%`
        );
      }

      query = query.order('created_at', { ascending: false });

      if (params?.limit) {
        const from = params.offset || 0;
        const to = from + params.limit - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;
      if (error) {
        console.error('Error fetching admin products:', error.message);
        return { products: [], total: 0 };
      }
      return { products: (data || []).map(mapProduct), total: count || 0 };
    } catch (err: any) {
      console.error('Admin products fetch failed:', err.message);
      return { products: [], total: 0 };
    }
  },

  async createProduct(input: ProductInput): Promise<{ product: Product | null; error: string | null }> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('products')
        .insert([input])
        .select()
        .single();
      if (error) return { product: null, error: error.message };
      return { product: mapProduct(data), error: null };
    } catch (err: any) {
      return { product: null, error: err.message };
    }
  },

  async updateProduct(id: string, input: Partial<ProductInput>): Promise<{ product: Product | null; error: string | null }> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('products')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (error) return { product: null, error: error.message };
      if (!data) return { product: null, error: 'Product not found or update was blocked.' };
      return { product: mapProduct(data), error: null };
    } catch (err: any) {
      return { product: null, error: err.message };
    }
  },

  async deleteProduct(id: string): Promise<{ error: string | null }> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) return { error: error.message };
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  },

  async toggleFeatured(id: string, isFeatured: boolean): Promise<{ error: string | null }> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('products')
        .update({ is_featured: isFeatured, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) return { error: error.message };
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  },

  async toggleActive(id: string, isActive: boolean): Promise<{ error: string | null }> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('products')
        .update({ is_active: isActive, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) return { error: error.message };
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  },

  async createCategory(input: { name: string; slug: string; description?: string; icon?: string }): Promise<{ category: ProductCategory | null; error: string | null }> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .insert([input])
        .select()
        .single();
      if (error) return { category: null, error: error.message };
      return { category: mapCategory(data), error: null };
    } catch (err: any) {
      return { category: null, error: err.message };
    }
  },

  async updateCategory(id: string, input: Partial<{ name: string; slug: string; description: string; icon: string }>): Promise<{ error: string | null }> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('product_categories').update(input).eq('id', id);
      if (error) return { error: error.message };
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  },

  async deleteCategory(id: string): Promise<{ error: string | null }> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('product_categories').delete().eq('id', id);
      if (error) return { error: error.message };
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  },
};
